import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  GripVertical,
  Plus,
  X,
  Loader2,
  Save,
  Search,
  Star,
  Sparkles,
  Power,
  Trash2,
  RotateCcw,
  ListChecks,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import api, {
  type ArcheManagementSummary,
  type ArcheLifecycleState,
  type ArcheKindFilter,
} from '@/lib/api';

// =========================================================================
// Internal vs plugin
// =========================================================================

/**
 * Platform-built arches are everything the sandboxed plugin renderer doesn't
 * draw — the same `render_mode` discriminator the runtime uses. Contributor id
 * would be wrong here: a few platform arches are owned by the system account
 * rather than the Fias platform account.
 */
function isInternalArche(arche: { renderMode: string }): boolean {
  return arche.renderMode !== 'plugin';
}

const KIND_LABELS: Record<ArcheKindFilter, string> = {
  all: 'All arches',
  internal: 'Internal only',
  plugin: 'Plugins only',
};

function KindSelect({
  value,
  onChange,
  className = '',
}: {
  value: ArcheKindFilter;
  onChange: (next: ArcheKindFilter) => void;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ArcheKindFilter)}
      aria-label="Filter by arche origin"
      className={`h-9 rounded-md border bg-background px-3 text-sm ${className}`}
    >
      {(Object.keys(KIND_LABELS) as ArcheKindFilter[]).map((kind) => (
        <option key={kind} value={kind}>
          {KIND_LABELS[kind]}
        </option>
      ))}
    </select>
  );
}

function matchesKind(arche: ArcheManagementSummary, kind: ArcheKindFilter): boolean {
  if (kind === 'all') return true;
  return kind === 'internal' ? isInternalArche(arche) : !isInternalArche(arche);
}

/** Case-insensitive match over the two fields an admin actually types. */
function matchesSearch(arche: ArcheManagementSummary, lowerQuery: string): boolean {
  if (!lowerQuery) return true;
  return (
    arche.name.toLowerCase().includes(lowerQuery) || arche.archId.toLowerCase().includes(lowerQuery)
  );
}

// =========================================================================
// Sortable Featured Item
// =========================================================================

/** The minimum a row needs — see `resolveOrPlaceholder` for why it isn't the
 * full summary. */
interface SortableRowArche {
  archId: string;
  name: string;
  imageRef: string | null;
}

/**
 * Featured / suggested lists are stored as bare id arrays, so an id whose arche
 * isn't in the loaded pool used to render nothing at all — an invisible entry
 * that still got re-saved and that nobody could remove. Fall back to showing
 * the raw id instead.
 */
function resolveOrPlaceholder(
  archId: string,
  archeMap: Map<string, ArcheManagementSummary>,
): SortableRowArche {
  return archeMap.get(archId) ?? { archId, name: archId, imageRef: null };
}

function SortableFeaturedItem({
  arche,
  onRemove,
}: {
  arche: SortableRowArche;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: arche.archId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded-md border bg-card"
    >
      <button {...attributes} {...listeners} className="cursor-grab p-1 text-muted-foreground">
        <GripVertical className="h-4 w-4" />
      </button>
      {arche.imageRef ? (
        <ArcheIconImg
          imageRef={arche.imageRef}
          size={64}
          alt={arche.name}
          className="w-6 h-6 rounded"
        />
      ) : (
        <DefaultArcheIcon className="w-6 h-6 rounded" />
      )}
      <span className="text-sm flex-1 truncate">{arche.name}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={() => onRemove(arche.archId)}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

// =========================================================================
// All Arches tab — admin lifecycle (disable / enable / delete / restore)
// =========================================================================

type LifecycleAction = 'disable' | 'enable' | 'delete' | 'restore' | 'unlist' | 'list';

interface PendingAction {
  arche: ArcheManagementSummary;
  action: LifecycleAction;
}

function lifecycleBadgeVariant(
  state: ArcheLifecycleState,
): 'default' | 'secondary' | 'destructive' {
  switch (state) {
    case 'active':
      return 'default';
    case 'disabled':
      return 'secondary';
    case 'deleted':
      return 'destructive';
  }
}

function AllArchesTab() {
  const { toast } = useToast();
  const [arches, setArches] = useState<ArcheManagementSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<'all' | ArcheLifecycleState>('all');
  const [kindFilter, setKindFilter] = useState<ArcheKindFilter>('all');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.adminGetArchesSummary({
        limit: 500,
        lifecycle: lifecycleFilter,
        search: search.trim() || undefined,
        kind: kindFilter,
      });
      const sorted = [...result.arches].sort((a, b) => a.name.localeCompare(b.name));
      setArches(sorted);
    } catch (err) {
      toast({ title: 'Failed to load arches', variant: 'destructive' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [lifecycleFilter, kindFilter, search, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onConfirm = useCallback(async () => {
    if (!pending) return;
    const { arche, action } = pending;
    try {
      setSubmitting(true);
      const trimmed = reason.trim();
      switch (action) {
        case 'disable':
          await api.adminDisableArche(arche.archId, trimmed || undefined);
          toast({ title: `Disabled "${arche.name}"` });
          break;
        case 'enable':
          await api.adminEnableArche(arche.archId);
          toast({ title: `Enabled "${arche.name}"` });
          break;
        case 'delete': {
          const result = await api.adminDeleteArche(arche.archId, trimmed || undefined);
          toast({
            title: result.hardDeleted
              ? `Deleted "${arche.name}" (hard)`
              : `Deleted "${arche.name}" (soft, row preserved)`,
          });
          break;
        }
        case 'restore':
          await api.adminRestoreArche(arche.archId);
          toast({ title: `Restored "${arche.name}" to disabled` });
          break;
        case 'unlist':
          await api.adminUnlistArche(arche.archId, trimmed || undefined);
          toast({ title: `Unlisted "${arche.name}"` });
          break;
        case 'list':
          await api.adminListArche(arche.archId);
          toast({ title: `Listed "${arche.name}"` });
          break;
      }
      setPending(null);
      setReason('');
      await reload();
    } catch (err) {
      toast({
        title: 'Action failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }, [pending, reason, reload, toast]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">All Arches</CardTitle>
        <CardDescription>
          Search across every arche on the platform. Unlist hides an arche from the store and search
          but keeps it runnable for anyone with the URL; disable temporarily blocks runtime use
          entirely; delete tombstones it (rows are preserved for accounting). Re-enable does NOT
          auto-relist — contributors must explicitly re-list via the publish flow.
        </CardDescription>
        <div className="flex flex-col md:flex-row gap-2 mt-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, arche ID, or contributor username / ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <select
            value={lifecycleFilter}
            onChange={(e) => setLifecycleFilter(e.target.value as 'all' | ArcheLifecycleState)}
            className="h-9 rounded-md border bg-background px-3 text-sm"
          >
            <option value="all">All lifecycle states</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="deleted">Deleted</option>
          </select>
          <KindSelect value={kindFilter} onChange={setKindFilter} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : arches.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No matching arches.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-2">Name</th>
                  <th className="py-2 pr-2">Origin</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Lifecycle</th>
                  <th className="py-2 pr-2">Listed</th>
                  <th className="py-2 pr-2">Contributor</th>
                  <th className="py-2 pl-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {arches.map((arche) => (
                  <tr key={arche.archId} className="border-b/50">
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-2">
                        {arche.imageRef ? (
                          <ArcheIconImg
                            imageRef={arche.imageRef}
                            size={64}
                            alt={arche.name}
                            className="w-6 h-6 rounded"
                          />
                        ) : (
                          <DefaultArcheIcon className="w-6 h-6 rounded" />
                        )}
                        <div className="min-w-0">
                          <div className="truncate font-medium">{arche.name}</div>
                          <div className="text-xs text-muted-foreground truncate font-mono">
                            {arche.archId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-2">
                      {isInternalArche(arche) ? (
                        <Badge variant="secondary" className="text-xs" title={arche.renderMode}>
                          internal
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          plugin
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      <Badge variant="outline" className="text-xs">
                        {arche.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-2">
                      <Badge
                        variant={lifecycleBadgeVariant(arche.lifecycleState)}
                        className="text-xs"
                      >
                        {arche.lifecycleState}
                      </Badge>
                    </td>
                    <td className="py-2 pr-2 text-xs">
                      {arche.isListed ? 'yes' : <span className="text-muted-foreground">no</span>}
                    </td>
                    <td className="py-2 pr-2 text-xs truncate max-w-[160px]">
                      {arche.contributorUsername ? (
                        <span title={arche.contributorId ?? undefined}>
                          {arche.contributorUsername}
                        </span>
                      ) : arche.contributorId ? (
                        <span
                          className="font-mono text-muted-foreground"
                          title={arche.contributorId}
                        >
                          {arche.contributorId}
                        </span>
                      ) : (
                        <span className="text-muted-foreground" title="No contributor">
                          —
                        </span>
                      )}
                    </td>
                    <td className="py-2 pl-2 text-right space-x-1">
                      {arche.lifecycleState === 'active' && arche.isListed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'unlist' })}
                          className="gap-1"
                        >
                          <EyeOff className="h-3 w-3" /> Unlist
                        </Button>
                      )}
                      {arche.lifecycleState === 'active' && !arche.isListed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'list' })}
                          className="gap-1"
                        >
                          <Eye className="h-3 w-3" /> List
                        </Button>
                      )}
                      {arche.lifecycleState === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'disable' })}
                          className="gap-1"
                        >
                          <Power className="h-3 w-3" /> Disable
                        </Button>
                      )}
                      {arche.lifecycleState === 'disabled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'enable' })}
                          className="gap-1"
                        >
                          <Power className="h-3 w-3" /> Enable
                        </Button>
                      )}
                      {arche.lifecycleState === 'deleted' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'restore' })}
                          className="gap-1"
                        >
                          <RotateCcw className="h-3 w-3" /> Restore
                        </Button>
                      )}
                      {arche.lifecycleState !== 'deleted' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setPending({ arche, action: 'delete' })}
                          className="gap-1"
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Confirmation modal */}
      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !submitting && setPending(null)}
        >
          <div
            className="bg-background border rounded-lg shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">
              {pending.action === 'disable' && 'Disable arche?'}
              {pending.action === 'enable' && 'Enable arche?'}
              {pending.action === 'delete' && 'Delete arche?'}
              {pending.action === 'restore' && 'Restore arche?'}
              {pending.action === 'unlist' && 'Unlist arche?'}
              {pending.action === 'list' && 'List arche?'}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{pending.arche.name}</span>
              {' — '}
              {pending.action === 'disable' &&
                'temporarily removes this arche from the store and blocks runtime use. Reversible.'}
              {pending.action === 'enable' &&
                'returns the arche to active. Does NOT re-list it; contributor must explicitly relist.'}
              {pending.action === 'delete' &&
                (pending.arche.status === 'draft'
                  ? 'permanently hard-deletes this draft.'
                  : 'tombstones the arche. The row stays so creator earnings, sessions, and entitlements still join, but every public surface treats it as gone.')}
              {pending.action === 'restore' &&
                'returns the arche to disabled (not active). The contributor must re-enable + relist to bring it back to the store.'}
              {pending.action === 'unlist' &&
                'hides this arche from the store, search, and featured lists. Anyone with the URL can still open and use it. Reversible.'}
              {pending.action === 'list' &&
                'puts this arche back in the store and search. Fails if another contributor has taken the name during the unlist window.'}
            </p>
            {(pending.action === 'disable' ||
              pending.action === 'delete' ||
              pending.action === 'unlist') && (
              <div className="mt-4">
                <label htmlFor="reason" className="text-sm font-medium">
                  Reason (optional)
                </label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Recorded in the audit log"
                  className="mt-1"
                  maxLength={500}
                />
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setPending(null);
                  setReason('');
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant={pending.action === 'delete' ? 'destructive' : 'default'}
                onClick={onConfirm}
                disabled={submitting}
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// =========================================================================
// Main Component
// =========================================================================

export function ArcheManagement() {
  const { toast } = useToast();

  // All arches for pickers
  const [allArches, setAllArches] = useState<ArcheManagementSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // Featured arches state
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);
  const [featuredDirty, setFeaturedDirty] = useState(false);
  const [featuredSaving, setFeaturedSaving] = useState(false);
  const [featuredSearch, setFeaturedSearch] = useState('');

  // Suggested arches state
  const [selectedArcheId, setSelectedArcheId] = useState<string | null>(null);
  const [suggestedIds, setSuggestedIds] = useState<string[]>([]);
  const [suggestedDirty, setSuggestedDirty] = useState(false);
  const [suggestedSaving, setSuggestedSaving] = useState(false);
  const [suggestedSearch, setSuggestedSearch] = useState('');
  const [suggestedKind, setSuggestedKind] = useState<ArcheKindFilter>('all');

  // Which arche's suggestions are being edited — the left-hand picker.
  const [archeListSearch, setArcheListSearch] = useState('');
  const [archeListKind, setArcheListKind] = useState<ArcheKindFilter>('all');

  // Fetch all arches + featured list on mount
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Two windows, merged. The plain listing is `created_at DESC LIMIT 500`,
      // so once enough developer plugins accumulate the platform's own arches —
      // the OLDEST rows in the table — fall off the end and disappear from every
      // picker on this page. The internal set is small and bounded, so asking
      // for it explicitly keeps it present no matter how many plugins exist.
      const [internalResult, recentResult, featuredResult] = await Promise.all([
        api.adminGetArchesSummary({ limit: 500, kind: 'internal' }),
        api.adminGetArchesSummary({ limit: 500 }),
        api.adminGetFeaturedArches(),
      ]);
      const byId = new Map<string, ArcheManagementSummary>();
      for (const arche of [...internalResult.arches, ...recentResult.arches]) {
        byId.set(arche.archId, arche);
      }
      setAllArches([...byId.values()].sort((a, b) => a.name.localeCompare(b.name)));
      setFeaturedIds(featuredResult.featured.map((f) => f.archId));
    } catch (err) {
      toast({ title: 'Failed to load data', variant: 'destructive' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Arche lookup map
  const archeMap = useMemo(() => {
    const map = new Map<string, ArcheManagementSummary>();
    for (const a of allArches) map.set(a.archId, a);
    return map;
  }, [allArches]);

  // =========================================================================
  // Featured Arches handlers
  // =========================================================================

  const featuredArches = useMemo(
    () => featuredIds.map((id) => resolveOrPlaceholder(id, archeMap)),
    [featuredIds, archeMap],
  );

  const featuredSet = useMemo(() => new Set(featuredIds), [featuredIds]);

  const publishedArches = useMemo(
    () => allArches.filter((a) => a.status === 'published'),
    [allArches],
  );

  const filteredAvailableArches = useMemo(() => {
    const q = featuredSearch.toLowerCase();
    return publishedArches.filter(
      (a) =>
        !featuredSet.has(a.archId) &&
        (a.name.toLowerCase().includes(q) || a.archId.toLowerCase().includes(q)),
    );
  }, [publishedArches, featuredSet, featuredSearch]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleFeaturedDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFeaturedIds((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      return arrayMove(prev, oldIndex, newIndex);
    });
    setFeaturedDirty(true);
  }, []);

  const addFeatured = useCallback((archId: string) => {
    setFeaturedIds((prev) => [...prev, archId]);
    setFeaturedDirty(true);
  }, []);

  const removeFeatured = useCallback((archId: string) => {
    setFeaturedIds((prev) => prev.filter((id) => id !== archId));
    setFeaturedDirty(true);
  }, []);

  const saveFeatured = useCallback(async () => {
    try {
      setFeaturedSaving(true);
      const result = await api.adminSetFeaturedArches(featuredIds);
      setFeaturedIds(result.featured.map((f) => f.archId));
      setFeaturedDirty(false);
      toast({ title: 'Featured arches saved' });
    } catch (err) {
      toast({ title: 'Failed to save', variant: 'destructive' });
      console.error(err);
    } finally {
      setFeaturedSaving(false);
    }
  }, [featuredIds, toast]);

  // =========================================================================
  // Suggested Arches handlers
  // =========================================================================

  const loadSuggested = useCallback(async (archId: string) => {
    setSelectedArcheId(archId);
    setSuggestedDirty(false);
    setSuggestedSearch('');
    try {
      const result = await api.adminGetSuggestedArches(archId);
      setSuggestedIds(result.suggestedArches);
    } catch {
      setSuggestedIds([]);
    }
  }, []);

  const suggestedArches = useMemo(
    () => suggestedIds.map((id) => resolveOrPlaceholder(id, archeMap)),
    [suggestedIds, archeMap],
  );

  const suggestedSet = useMemo(() => new Set(suggestedIds), [suggestedIds]);

  const filteredSuggestableArches = useMemo(() => {
    const q = suggestedSearch.trim().toLowerCase();
    return allArches.filter(
      (a) =>
        a.archId !== selectedArcheId &&
        !suggestedSet.has(a.archId) &&
        matchesKind(a, suggestedKind) &&
        matchesSearch(a, q),
    );
  }, [allArches, selectedArcheId, suggestedSet, suggestedSearch, suggestedKind]);

  /** The left-hand "which arche am I editing" list. */
  const selectableArches = useMemo(() => {
    const q = archeListSearch.trim().toLowerCase();
    return allArches.filter((a) => matchesKind(a, archeListKind) && matchesSearch(a, q));
  }, [allArches, archeListKind, archeListSearch]);

  const handleSuggestedDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSuggestedIds((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      return arrayMove(prev, oldIndex, newIndex);
    });
    setSuggestedDirty(true);
  }, []);

  const addSuggested = useCallback((archId: string) => {
    setSuggestedIds((prev) => [...prev, archId]);
    setSuggestedDirty(true);
  }, []);

  const removeSuggested = useCallback((archId: string) => {
    setSuggestedIds((prev) => prev.filter((id) => id !== archId));
    setSuggestedDirty(true);
  }, []);

  const saveSuggested = useCallback(async () => {
    if (!selectedArcheId) return;
    try {
      setSuggestedSaving(true);
      const result = await api.adminSetSuggestedArches(selectedArcheId, suggestedIds);
      setSuggestedIds(result.suggestedArches);
      setSuggestedDirty(false);
      toast({ title: 'Suggested arches saved' });
    } catch (err) {
      toast({ title: 'Failed to save', variant: 'destructive' });
      console.error(err);
    } finally {
      setSuggestedSaving(false);
    }
  }, [selectedArcheId, suggestedIds, toast]);

  // =========================================================================
  // Render
  // =========================================================================

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-4 sm:p-8">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-8">
      <div className="mb-6">
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Dev Console
        </Link>
        <h1 className="text-3xl font-bold">Arche Management</h1>
        <p className="mt-1 text-muted-foreground">
          Manage featured arches on the marketplace and per-arche suggested arches in the sidebar.
        </p>
      </div>

      <Tabs defaultValue="featured">
        {/* Wrap tabs onto multiple rows on a narrow screen rather than
            overflowing the page. */}
        <TabsList className="flex h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="featured" className="gap-1.5">
            <Star className="h-4 w-4" />
            Featured Arches
          </TabsTrigger>
          <TabsTrigger value="suggested" className="gap-1.5">
            <Sparkles className="h-4 w-4" />
            Suggested Arches
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1.5">
            <ListChecks className="h-4 w-4" />
            All Arches
          </TabsTrigger>
        </TabsList>

        {/* ============================================================ */}
        {/* Featured Arches Tab */}
        {/* ============================================================ */}
        <TabsContent value="featured" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Available arches */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Available Arches</CardTitle>
                <CardDescription>Click + to add an arche to the featured list</CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search arches..."
                    value={featuredSearch}
                    onChange={(e) => setFeaturedSearch(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-1">
                {filteredAvailableArches.map((arche) => (
                  <div
                    key={arche.archId}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted"
                  >
                    {arche.imageRef ? (
                      <ArcheIconImg
                        imageRef={arche.imageRef}
                        size={64}
                        alt={arche.name}
                        className="w-6 h-6 rounded"
                      />
                    ) : (
                      <DefaultArcheIcon className="w-6 h-6 rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{arche.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {arche.archeType} · {arche.archId}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={() => addFeatured(arche.archId)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {filteredAvailableArches.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No matching arches
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Right: Current featured list (drag-to-reorder) */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">
                      Featured List ({featuredIds.length})
                    </CardTitle>
                    <CardDescription>Drag to reorder. Top arche appears first.</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={saveFeatured}
                    disabled={!featuredDirty || featuredSaving}
                    className="gap-1.5"
                  >
                    {featuredSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-1">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleFeaturedDragEnd}
                >
                  <SortableContext items={featuredIds} strategy={verticalListSortingStrategy}>
                    {featuredArches.map((arche) => (
                      <SortableFeaturedItem
                        key={arche.archId}
                        arche={arche}
                        onRemove={removeFeatured}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                {featuredIds.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No featured arches. Add some from the left panel.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================================ */}
        {/* Suggested Arches Tab */}
        {/* ============================================================ */}
        <TabsContent value="suggested" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Arche selector */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Arches ({selectableArches.length})</CardTitle>
                <CardDescription>Select an arche to edit its suggestions</CardDescription>
                <div className="mt-2 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or arche ID..."
                      value={archeListSearch}
                      onChange={(e) => setArcheListSearch(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  <KindSelect
                    value={archeListKind}
                    onChange={setArcheListKind}
                    className="w-full"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-0.5">
                {selectableArches.map((arche) => (
                  <button
                    key={arche.archId}
                    onClick={() => loadSuggested(arche.archId)}
                    className={`w-full flex items-center gap-2 p-2 rounded-md text-left transition-colors ${
                      selectedArcheId === arche.archId
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted'
                    }`}
                  >
                    {arche.imageRef ? (
                      <ArcheIconImg
                        imageRef={arche.imageRef}
                        size={64}
                        alt={arche.name}
                        className="w-6 h-6 rounded"
                      />
                    ) : (
                      <DefaultArcheIcon className="w-6 h-6 rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{arche.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{arche.archId}</div>
                    </div>
                    {(arche.suggestedArches?.length ?? 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {arche.suggestedArches.length}
                      </Badge>
                    )}
                  </button>
                ))}
                {selectableArches.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No matching arches
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Right: Suggested arches editor (spans 2 cols) */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">
                      {selectedArcheId
                        ? `Suggestions for ${archeMap.get(selectedArcheId)?.name ?? selectedArcheId}`
                        : 'Select an arche'}
                    </CardTitle>
                    <CardDescription>
                      These arches appear in the sidebar when a user is viewing the selected arche.
                    </CardDescription>
                  </div>
                  {selectedArcheId && (
                    <Button
                      size="sm"
                      onClick={saveSuggested}
                      disabled={!suggestedDirty || suggestedSaving}
                      className="gap-1.5"
                    >
                      {suggestedSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedArcheId ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Select an arche from the left panel to manage its suggestions.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {/* Current suggestions (drag to reorder) */}
                    <div>
                      <p className="text-sm font-medium mb-2">
                        Current suggestions ({suggestedIds.length})
                      </p>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleSuggestedDragEnd}
                      >
                        <SortableContext
                          items={suggestedIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-1">
                            {suggestedArches.map((arche) => (
                              <SortableFeaturedItem
                                key={arche.archId}
                                arche={arche}
                                onRemove={removeSuggested}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                      {suggestedIds.length === 0 && (
                        <p className="text-sm text-muted-foreground py-2">
                          No suggestions configured
                        </p>
                      )}
                    </div>

                    {/* Add suggestions */}
                    <div>
                      <p className="text-sm font-medium mb-2">Add suggestion</p>
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row">
                        <div className="relative flex-1">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search arches to add..."
                            value={suggestedSearch}
                            onChange={(e) => setSuggestedSearch(e.target.value)}
                            className="pl-9 h-9"
                          />
                        </div>
                        <KindSelect value={suggestedKind} onChange={setSuggestedKind} />
                      </div>
                      <div className="max-h-[300px] overflow-y-auto space-y-1 border rounded-md p-1">
                        {filteredSuggestableArches.slice(0, 50).map((arche) => (
                          <div
                            key={arche.archId}
                            className="flex items-center gap-2 p-2 rounded hover:bg-muted"
                          >
                            {arche.imageRef ? (
                              <ArcheIconImg
                                imageRef={arche.imageRef}
                                size={64}
                                alt={arche.name}
                                className="w-5 h-5 rounded"
                              />
                            ) : (
                              <DefaultArcheIcon className="w-5 h-5 rounded" />
                            )}
                            <span className="text-sm flex-1 truncate">{arche.name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => addSuggested(arche.archId)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        {filteredSuggestableArches.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-3">
                            No matching arches
                          </p>
                        )}
                        {/* The list is capped at 50 — say so, rather than
                            letting it look like the search found everything. */}
                        {filteredSuggestableArches.length > 50 && (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            Showing 50 of {filteredSuggestableArches.length} — narrow the search or
                            filter by origin.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================================ */}
        {/* All Arches Tab — admin lifecycle controls                     */}
        {/* ============================================================ */}
        <TabsContent value="all" className="mt-4">
          <AllArchesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ArcheManagement;
